import { ReactNode, useEffect, useMemo, useRef } from 'react'
import { useParams } from '@tanstack/react-router'
import ForbiddenError from '@/pages/errors/forbidden'
import { Skeleton } from '@deepguide-ai/ui/skeleton'
import { trpcClient } from '@/lib/trpc'
import {
  useWorkspacePermissions,
  WorkspaceRole,
} from '@/hooks/useWorkspacePermissions'

interface RequireRoleProps {
  /** Children to render if permission is granted */
  children: ReactNode
  /** Allowed roles (defaults to ADMIN and OWNER) */
  allowedRoles?: WorkspaceRole[]
  /** Resource being protected (for audit logging) */
  resource?: string
  /** Optional custom forbidden message */
  forbiddenMessage?: string
  /** Optional fallback to render when permission is denied */
  fallback?: ReactNode
}

/**
 * Route-level permission guard that renders 403 page for unauthorized users
 * and logs access denial attempts for audit trail.
 *
 * @example
 * ```tsx
 * // In a route component:
 * <RequireRole allowedRoles={['ADMIN', 'OWNER']} resource="settings">
 *   <SettingsPage />
 * </RequireRole>
 * ```
 */
export function RequireRole({
  children,
  allowedRoles = ['ADMIN', 'OWNER'],
  resource = 'protected-resource',
  fallback,
}: RequireRoleProps) {
  const { workspaceId } = useParams({ strict: false }) as {
    workspaceId?: string
  }
  const { role, isLoading } = useWorkspacePermissions(workspaceId)
  const requiredRoles = useMemo(
    () =>
      allowedRoles.filter((r): r is NonNullable<WorkspaceRole> => r !== null),
    [allowedRoles]
  )
  const hasPermission = !!role && allowedRoles.includes(role)
  const logKey =
    !isLoading && !hasPermission && workspaceId && role
      ? `${workspaceId}:${role}:${resource}:${requiredRoles.join(',')}`
      : null
  const lastLoggedKey = useRef<string | null>(null)

  useEffect(() => {
    if (!logKey || !role || lastLoggedKey.current === logKey) return

    lastLoggedKey.current = logKey
    trpcClient.audit.logAccessDenied
      .mutate({
        resource,
        attemptedRole: role,
        requiredRoles,
      })
      .catch(() => {
        // Silently fail - don't block UI for audit logging errors
      })
  }, [logKey, requiredRoles, resource, role])

  // Show loading state while checking permissions
  if (isLoading) {
    return (
      <div className='flex h-full w-full items-center justify-center p-8'>
        <div className='w-full max-w-md space-y-4'>
          <Skeleton className='h-8 w-3/4' />
          <Skeleton className='h-4 w-full' />
          <Skeleton className='h-4 w-2/3' />
        </div>
      </div>
    )
  }

  if (!hasPermission) {
    return fallback ?? <ForbiddenError />
  }

  return <>{children}</>
}

/**
 * Convenience wrapper for admin-only pages
 */
export function RequireAdmin({
  children,
  resource,
  fallback,
}: {
  children: ReactNode
  resource?: string
  fallback?: ReactNode
}) {
  return (
    <RequireRole
      allowedRoles={['ADMIN', 'OWNER']}
      resource={resource}
      fallback={fallback}
    >
      {children}
    </RequireRole>
  )
}

/**
 * Convenience wrapper for contributor+ pages
 */
export function RequireContributor({
  children,
  resource,
  fallback,
}: {
  children: ReactNode
  resource?: string
  fallback?: ReactNode
}) {
  return (
    <RequireRole
      allowedRoles={['ADMIN', 'OWNER', 'CONTRIBUTOR']}
      resource={resource}
      fallback={fallback}
    >
      {children}
    </RequireRole>
  )
}
